// Listing-mode switcher for the Opportunities module.
//
// Mirrors the CURRENT prod pattern (`src/inventory/InventoryLayout.tsx` on
// aeronetsoftware/cloud/aeronet-frontend@develop, commits bd7026e5 / 0c60b76e):
// a breadcrumb whose last segment is a hover/click dropdown built from the
// shared <BreadcrumbDropdownMenu> + <BreadcrumbMenuLinkItem>, with the modes as
// ROUTES and an <Outlet/> beneath.
//
// This replaces the previous local-state switcher (an outlined Chip + raw MUI
// Menu, mode held in useState + localStorage, every mode rendered inside one
// route). Two things that bought us: the mode is now linkable/bookmarkable and
// survives a refresh via the URL rather than localStorage, and the chrome comes
// from the shared component instead of a per-module copy — the same drift that
// left six modules each carrying their own hand-rolled switcher.
import { useEffect, useState } from "react";

import { Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import { useDataGridStore } from "../store/DataGrid";
import { MODE_STORAGE_KEY } from "./modes";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

// Absolute router paths (the SPA is mounted under a /app basename, which
// react-router strips — so these match prod's "/inventory/parts" shape).
export const breadcrumbMenu: { [key: string]: string } = {
  "/opportunities/pipeline": "Pipeline",
  "/opportunities/list": "List",
  "/opportunities/dashboard": "Dashboard",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 *
 * Keyed by ABSOLUTE path, exactly like `breadcrumbMenu`.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  "/opportunities/pipeline": "Deals by stage",
  "/opportunities/list": "Every opportunity",
  "/opportunities/dashboard": "Pipeline value and KPIs",
};

const allRoutes: { [key: string]: string } = {
  "/opportunities": "Opportunities",
  ...breadcrumbMenu,
};

const OpportunityLayout: React.FC = () => {
  const location = useLocation();
  // Module base for the shared list toolbar. The three modes below each add a
  // URL segment, so a relative navigate from inside one resolves a level too
  // deep — OpportunityList's Create FAB was landing on /opportunities/list/create,
  // which matches no route and renders blank. Derived from the live pathname
  // rather than reusing the "/opportunities" literals above, so it stays right
  // if this module is ever mounted elsewhere by the nav.
  const listingBase = breadcrumbMenu[location.pathname]
    ? location.pathname.slice(0, location.pathname.lastIndexOf("/"))
    : location.pathname;
  const resetQuickFilter = useDataGridStore((s) => s.resetQuickFilter);
  const popupState = usePopupState({
    variant: "popover",
    popupId: "opportunityBreadcrumbMenu",
  });

  const [pathLinks, setPathLinks] = useState<string[]>([]);

  // Remember the last mode so landing on bare /opportunities returns the user
  // where they left off — the one behaviour worth keeping from the old
  // localStorage switcher. index.tsx reads this to pick its redirect target.
  useEffect(() => {
    if (breadcrumbMenu[location.pathname]) {
      window.localStorage.setItem(MODE_STORAGE_KEY, location.pathname);
    }
  }, [location.pathname]);

  // Accumulate "/a", "/a/b", … from the current path so each ancestor segment
  // renders as a crumb link and the final one becomes the dropdown trigger.
  useEffect(() => {
    const _pathLinks: string[] = [];
    const paths = location.pathname.split("/");
    let pathLinkCount = 0;
    for (let i = 0; i < paths.length; i++) {
      let path = paths[i];
      const previousPath = _pathLinks[pathLinkCount - 1] ?? "";
      if (previousPath) {
        path = previousPath + "/" + path;
      } else if (path) {
        path = "/" + path;
      }
      if (path) {
        _pathLinks.push(path);
        pathLinkCount++;
      }
    }
    setPathLinks(_pathLinks);
  }, [location.pathname]);

  return (
    <>
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          {pathLinks.map((path) => {
            if (path === location.pathname) {
              return (
                <BreadcrumbHoverRoot key={path} popupState={popupState}>
                  <Button
                    endIcon={<BreadcrumbChevronIcon />}
                    sx={breadcrumbCurrentTriggerSx}
                  >
                    <Typography
                      variant="h2"
                      component="span"
                      sx={breadcrumbCurrentLabelSx}
                    >
                      {allRoutes[location.pathname]}
                    </Typography>
                  </Button>
                  <BreadcrumbDropdownMenu
                    popupState={popupState}
                    headerTitle="Opportunities"
                  >
                    {Object.entries(breadcrumbMenu).map(([path, label]) => (
                      <BreadcrumbMenuLinkItem
                        key={path}
                        to={path}
                        label={label}
                        description={describeMode(MODE_DESCRIPTIONS, path)}
                        selected={location.pathname === path}
                        onClick={() => {
                          // Each mode is a different grid — carrying the
                          // previous mode's quick filter across would silently
                          // hide rows.
                          resetQuickFilter();
                          popupState.close();
                        }}
                      />
                    ))}
                  </BreadcrumbDropdownMenu>
                </BreadcrumbHoverRoot>
              );
            }
            return (
              <LinkRouter to={path} key={path}>
                {allRoutes[path]} /
              </LinkRouter>
            );
          })}
        </Title>
      </BreadcrumbHeaderListing>
      {/* createSegment is "new": this module declares its create route as
          `new` (index.tsx), not `create`. Without that the FAB would resolve
          to /opportunities/create, which matches no route either. */}
      <ListingRouteBaseProvider
        value={{ base: listingBase, createSegment: "new" }}
      >
        <Outlet />
      </ListingRouteBaseProvider>
    </>
  );
};

export default OpportunityLayout;
